import { Autocomplete, Grid2, InputAdornment, TextField } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { setDisplayErrorSnackBar, setIsLoading } from '../../../../../features/userSlice';
import { websiteTargetEndPointLogic } from '../../../../../logic/AssetManagement/WebsiteTarget/websiteTargetEndPointLogic';
import { NumericFormat } from "react-number-format";
import store from '../../../../../store';

interface Props {
  oauthTokenHeader: any;
  onAddMicroTarget?: (data: any) => void;
  index?: number; // Add index prop to track which micro target this is
}

const AddMicroTarget = forwardRef<any, Props>((props, ref) => {
  const { oauthTokenHeader, index = 0 } = props; // Default to 0 if no index provided
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (_: unknown, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const [Geos, setGeos] = useState([]) as any;
  const [selectedGeo, setSelectedGeo] = useState(null) as any;
  const [Products, setProducts] = useState([]) as any;
  const [selectedProduct, setSelectedProduct] = useState(null) as any;
  const [microFtd, setMicroFtd] = useState(0) as any;
  const [microProfit, setMicroProfit] = useState(0) as any;
  const [microNrpu, setMicroNrpu] = useState(0) as any;
  const [microSpend, setMicroSpend] = useState(0) as any;
  const [microClicks, setMicroClicks] = useState(0) as any;
  const [microCvr, setMicroCvr] = useState(0) as any;
  const [microCac, setMicroCac] = useState(0) as any;
  const [microCpa, setMicroCpa] = useState(0) as any;
  const [microRs, setMicroRs] = useState(0) as any;
  const [microTotalIncome, setMicroTotalIncome] = useState(0) as any;
  const [microCpaPv, setMicroCpaPv] = useState(0) as any;
  const [microRsPv, setMicroRsPv] = useState(0) as any;
  const [microPv, setMicroPv] = useState(0) as any;

  const getMicroTargetData = () => {
    if (!selectedGeo || !selectedProduct) {
      return null;
    }
    const data = {
      geo: selectedGeo?.name,
      product_type: selectedProduct?.name,
      ftd: Math.abs(parseFloat(microFtd)),
      profit: Math.abs(parseFloat(microProfit)),
      nrpu: Math.abs(parseFloat(microNrpu)),
      spend: Math.abs(parseFloat(microSpend)),
      clicks: Math.abs(parseFloat(microClicks)),
      cvr: Math.abs(parseFloat(microCvr)),
      cac: Math.abs(parseFloat(microCac)),
      cpa: Math.abs(parseFloat(microCpa)),
      rs: Math.abs(parseFloat(microRs)),
      total_income: Math.abs(parseFloat(microTotalIncome)),
      cpa_pv: Math.abs(parseFloat(microCpaPv)),
      rs_pv: Math.abs(parseFloat(microRsPv)),
      pv: Math.abs(parseFloat(microPv)),
    };
    return data;
  };

  useImperativeHandle(ref, () => ({
    getMicroTargetData: () => getMicroTargetData(),
  }));

  useEffect(() => {
    const fetchGeos = async () => {
      setIsLoading(true);
      try {
        await websiteTargetEndPointLogic.getGeo(oauthTokenHeader, setGeos);
      }
      catch (error: any) {
        store.dispatch(setDisplayErrorSnackBar({ message: error?.parsedText || 'Unknown error occurred', type: 'error' }));
      }
      finally {
        setIsLoading(false);
      }
    };
    if (oauthTokenHeader) {
      fetchGeos();
    }
  }, [oauthTokenHeader]);

  useEffect(() => {
    const fetchProductType = async () => {
      setIsLoading(true);
      try {
        await websiteTargetEndPointLogic.getProductType(oauthTokenHeader, setProducts);
      }
      catch (error: any) {
        store.dispatch(setDisplayErrorSnackBar({ message: error?.parsedText || 'Unknown error occurred', type: 'error' }));
      }
      finally {
        setIsLoading(false);
      }
    };
    if (oauthTokenHeader) {
      fetchProductType();
    }
  }, [oauthTokenHeader]);

  const GeoList = Geos.map((geo: any) => {
    return { name: geo.name, uid: geo.shared_uid };
  });

  const ProductTypes = Products.map((product: any) => {
    return { name: product.type_name, uid: product.shared_uid };
  });

  return (
    <>
      <Grid2 container columnSpacing={0}>
        <Grid2 size={11}>
          <Accordion
            expanded={expanded === "panel1"}
            onChange={handleChange("panel1")}
            className="AddMicroTargetWrap"
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon color="secondary" />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
                Micro - Target #{index + 1}
              </Typography>
              <Typography component="span" sx={{ color: "text.secondary" }}>
                Geo & Target based metrics
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid2 container mt={1} columnSpacing={3} mb={1}>
                <Grid2 size={12}>
                  <Typography fontSize={16} mb={1}>
                    General:
                  </Typography>
                </Grid2>
                <Grid2 size={4}>
                  <Autocomplete
                    sx={{ width: "100%" }}
                    options={GeoList}
                    getOptionLabel={(option: any) => option?.name}
                    value={selectedGeo}
                    onChange={(_, newValue) => {
                      setSelectedGeo(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Geo"
                        label="Geo"
                        required
                        InputProps={{
                          ...params.InputProps,
                          readOnly: true,
                        }}
                        color="secondary"
                      />
                    )}
                    size="small"
                    freeSolo={false}
                  />
                </Grid2>
                <Grid2 size={4}>
                  <Autocomplete
                    sx={{ width: "100%" }}
                    options={ProductTypes}
                    getOptionLabel={(option: any) => option?.name}
                    value={selectedProduct}
                    onChange={(_, newValue) => {
                      setSelectedProduct(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Product"
                        label="Product"
                        required
                        InputProps={{
                          ...params.InputProps,
                          readOnly: true,
                        }}
                        color="secondary"
                      />
                    )}
                    size="small"
                    freeSolo={false}
                  />
                </Grid2>
              </Grid2>
              <Grid2 container mt={3} columnSpacing={3}>
                <Grid2 size={12}>
                  <Typography fontSize={16} mb={1}>
                    Key metrics:
                  </Typography>
                </Grid2>
                <Grid2 size={4}>
                  <NumericFormat
                    customInput={TextField}
                    label="FTD"
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    size="small"
                    value={microFtd}
                    onValueChange={(values) => setMicroFtd(values.value)}
                    thousandSeparator=","
                    decimalScale={2} // optional: limits to 2 decimal places
                    sx={{ width: "100%" }}
                  />
                </Grid2>
                <Grid2 size={4}>
                  <NumericFormat
                    customInput={TextField}
                    label="Profit"
                    placeholder='In USD'
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    size="small"
                    value={microProfit}
                    onValueChange={(values) => setMicroProfit(values.value)}
                    thousandSeparator=","
                    decimalScale={2} // optional: limits to 2 decimal places
                    sx={{ width: "100%" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid2>
                <Grid2 size={4}>
                  <NumericFormat
                    customInput={TextField}
                    label="NRPU"
                    placeholder="In USD"
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    size="small"
                    value={microNrpu}
                    onValueChange={(values) => setMicroNrpu(values.value)}
                    thousandSeparator=","
                    decimalScale={2} // optional: limits to 2 decimal places
                    sx={{ width: "100%" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid2>
                <Grid2 size={4} mt={2}>
                  <NumericFormat
                    customInput={TextField}
                    label="Spend"
                    placeholder="In USD"
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    size="small"
                    value={microSpend}
                    onValueChange={(values) => setMicroSpend(values.value)}
                    thousandSeparator=","
                    decimalScale={2} // optional: limits to 2 decimal places
                    sx={{ width: "100%" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid2>
                <Grid2 size={4} mt={2}>
                  <NumericFormat
                    customInput={TextField}
                    label="Clicks"
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    size="small"
                    value={microClicks}
                    onValueChange={(values) => setMicroClicks(values.value)}
                    thousandSeparator=","
                    decimalScale={2} // optional: limits to 2 decimal places
                    sx={{ width: "100%" }}
                  />
                </Grid2>
                <Grid2 size={4} mt={2}>
                  <NumericFormat
                    customInput={TextField}
                    label="CVR"
                    placeholder="In Percentage"
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    size="small"
                    value={microCvr}
                    onValueChange={(values) => setMicroCvr(values.value)}
                    isAllowed={(values) => {
                      const { formattedValue } = values;
                      // Allow digits, commas, and one decimal point
                      return /^[\d,]*\.?\d*$/.test(
                        formattedValue.replace(/,/g, "")
                      );
                    }}
                    thousandSeparator=","
                    decimalScale={2} // optional: limits to 2 decimal places
                    sx={{ width: "100%" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                  />
                </Grid2>
                <Grid2 size={4} mt={2}>
                  <NumericFormat
                    customInput={TextField}
                    label="CAC"
                    placeholder="In USD"
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    size="small"
                    value={microCac}
                    onValueChange={(values) => setMicroCac(values.value)}
                    thousandSeparator=","
                    decimalScale={2} // optional: limits to 2 decimal places
                    sx={{ width: "100%" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Typography fontSize={14} mt={1} sx={{ color: "#00000099" }}>
                    Fields marked with * are mandatory.
                  </Typography>
                </Grid2>
              </Grid2>
              <Grid2 container mt={3} columnSpacing={3}>
                <Grid2 size={12}>
                  <Typography fontSize={16} mb={1}>
                    Income:
                  </Typography>
                </Grid2>
                <Grid2 size={4}>
                  <NumericFormat
                    customInput={TextField}
                    label="CPA"
                    placeholder="In USD"
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    size="small"
                    value={microCpa}
                    onValueChange={(values) => setMicroCpa(values.value)}
                    thousandSeparator=","
                    decimalScale={2} // optional: limits to 2 decimal places
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid2>
                <Grid2 size={4}>
                  <NumericFormat
                    customInput={TextField}
                    label="RS"
                    placeholder="In USD"
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    size="small"
                    value={microRs}
                    onValueChange={(values) => setMicroRs(values.value)}
                    thousandSeparator=","
                    decimalScale={2} // optional: limits to 2 decimal places
                    sx={{ width: "100%" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid2>
                <Grid2 size={4}>
                  <NumericFormat
                    customInput={TextField}
                    label="Total Income"
                    placeholder="In USD"
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    size="small"
                    value={microTotalIncome}
                    onValueChange={(values) =>
                      setMicroTotalIncome(values.value)
                    }
                    thousandSeparator=","
                    decimalScale={2} // optional: limits to 2 decimal places
                    sx={{ width: "100%" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid2>
                <Grid2 size={4} mt={2}>
                  <NumericFormat
                    customInput={TextField}
                    label="CPA PV"
                    placeholder="In USD"
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    size="small"
                    value={microCpaPv}
                    onValueChange={(values) => setMicroCpaPv(values.value)}
                    thousandSeparator=","
                    decimalScale={2} // optional: limits to 2 decimal places
                    sx={{ width: "100%" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid2>
                <Grid2 size={4} mt={2}>
                  <NumericFormat
                    customInput={TextField}
                    label="RS PV"
                    placeholder="In USD"
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    size="small"
                    value={microRsPv}
                    onValueChange={(values) => setMicroRsPv(values.value)}
                    thousandSeparator=","
                    decimalScale={2} // optional: limits to 2 decimal places
                    sx={{ width: "100%" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid2>
                <Grid2 size={4} mt={2}>
                  <NumericFormat
                    customInput={TextField}
                    label="PV"
                    placeholder="In USD"
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    size="small"
                    value={microPv}
                    onValueChange={(values) => setMicroPv(values.value)}
                    thousandSeparator=","
                    decimalScale={2} // optional: limits to 2 decimal places
                    sx={{ width: "100%" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Typography fontSize={14} mt={1} sx={{ color: "#00000099" }}>
                    Fields marked with * are mandatory.
                  </Typography>
                </Grid2>
              </Grid2>
            </AccordionDetails>
          </Accordion>
        </Grid2>
      </Grid2>
    </>
  );
});

AddMicroTarget.displayName = "AddMicroTarget";

export default AddMicroTarget;
